import { useRef } from 'react';
import { apm } from '@elastic/apm-rum';
import dayjs from 'dayjs';
import { parseUserAgent } from 'react-device-detect';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { getDeviceIDFromCookie } from '../../common/device-cookie';
import { useAxiosWithAPI } from '../../common/hooks/useAxiosWithAPI';
import { UserAgentData } from '../../common/interfaces/user-agent';
import { getConfig } from '../../config';
import { getUserId } from '../../selectors/user';
import { GlintsTrackingClient } from './GlintsTrackingClient';
import { getGlintsTrackingSyncConfig } from './initializeGlintsTrackingClient';
import { getEventQueue, setEventQueue } from './trackEvent';
import {
  getGlintsTrackingIDFromCookie,
  setGlintsTrackingIDToCookie,
} from './trackingSessionUtils';
import { GlintsTrackingBatchEvent } from './types';

const GlintsTrackingSessionIDHeader = 'x-glints-tracking-session-id';

export default function useGlintsTracking() {
  const userId = useSelector(getUserId);
  const isSendingRef = useRef(false);
  const axios = useAxiosWithAPI();

  async function logEvent() {
    if (isSendingRef.current) return;
    try {
      isSendingRef.current = true;
      const events = [...getEventQueue()];
      if (events.length <= 0) return;
      const trackingID = getGlintsTrackingIDFromCookie();
      setGlintsTrackingIDToCookie(trackingID ?? uuidv4());
      const config = getConfig();
      const trackingClient = GlintsTrackingClient.getInstance();

      const userAgentData: UserAgentData = parseUserAgent(
        window.navigator.userAgent
      );
      const eventsToSend = events.slice(
        0,
        getGlintsTrackingSyncConfig().syncMaxEventBatchSize
      );

      /* eslint-disable camelcase */
      const batchEvent: GlintsTrackingBatchEvent = {
        batch_timestamp: dayjs().valueOf(),
        session_id: getGlintsTrackingIDFromCookie() as string,
        event_category: 'biz_employer_batch',
        events: eventsToSend,
        user_properties: {
          device_id: getDeviceIDFromCookie(),
          device_type: 'WEB',
          os_version: userAgentData.os?.version,
          os_name: userAgentData.os?.name,
          user_id: userId,
          exp_info: trackingClient.getExperimentationInfo(),
          country_code: String(config?.COUNTRY).toUpperCase(),
        },
      };
      /* eslint-enable camelcase */

      await axios.post('/glintsTracking/logging/batch', batchEvent, {
        headers: {
          [GlintsTrackingSessionIDHeader]: getGlintsTrackingIDFromCookie(),
        },
      });
      const currentQueue = getEventQueue();
      const sentEventIds = new Set(eventsToSend.map(event => event.event_id));
      const remainingEvents = currentQueue.filter(
        event => !sentEventIds.has(event.event_id)
      );
      setEventQueue(remainingEvents);
    } catch {
      try {
        apm.captureError('[GlintsTracking] Error sending event batch');
      } catch {
        // Failure reporting is also optional and must not reject the flush.
      }
    } finally {
      // eslint-disable-next-line require-atomic-updates
      isSendingRef.current = false;
    }
  }
  const logEventRef = useRef(logEvent);

  logEventRef.current = logEvent;

  return logEventRef;
}
